import VirtualizedTable from '@components/ListV2/components/table/VirtualizedTable';
import Icon from '@components/v2/ui/Icon/Icon';
import { Box } from '@mui/material';
import { Trash2 } from 'lucide-react';
import LangEnum from '../../../../lang/LangEnum';
import { CatalogTestIds } from '../../../../utils/testIds/catalog.testIds';
import {
  CatalogCreationAccessRow,
  CatalogCreationAccessRowDateChange,
  CatalogCreationAccessSelection,
} from '../catalogCreationAccess.types';
import { buildCatalogCreationAccessColumns } from '../columns/catalogCreationAccessList.columns';
import { useCatalogCreationAccessTable } from '../hooks/useCatalogCreationAccessTable';

interface CatalogCreationAccessTableProps {
  rows: CatalogCreationAccessRow[];
  maxVisibleRows?: number;
  canRemoveEntity: boolean;
  canEditDates: boolean;
  selection?: CatalogCreationAccessSelection;
  onEndReached?: () => void;
  onChangeRowDate: (rowDateChange: CatalogCreationAccessRowDateChange) => void;
  onRemoveRow: (entityId: string) => void;
}

const CatalogCreationAccessTable = ({
  rows,
  maxVisibleRows,
  canRemoveEntity,
  canEditDates,
  selection,
  onEndReached,
  onChangeRowDate,
  onRemoveRow,
}: CatalogCreationAccessTableProps) => {
  const { scrollElement, setScrollElement, tableHeight } = useCatalogCreationAccessTable({
    rowCount: rows.length,
    maxVisibleRows,
  });

  const columns = buildCatalogCreationAccessColumns({ canEditDates, onChangeRowDate });

  return (
    <Box
      ref={setScrollElement}
      sx={{
        height: tableHeight,
        overflowY: 'auto',
        '& .MuiTableCell-root:last-of-type': { boxShadow: 'none' },
      }}
    >
      {scrollElement && (
        <VirtualizedTable<CatalogCreationAccessRow>
          columns={columns}
          data={rows}
          customScrollParent={scrollElement}
          fixedTopOffset={0}
          hideColumnsButton
          checkboxProps={
            selection
              ? {
                  disableCheckBox: selection.isSelectionLocked,
                  isAllSelected: selection.isAllSelected,
                  selectedRows: selection.selectedIds,
                  onToggleSelectAll: selection.onToggleSelectAll,
                  onToggleSelectRow: (row) => selection.onToggleSelectRow(row.id),
                }
              : undefined
          }
          endReached={onEndReached ?? (() => undefined)}
          getRowActions={({ item }) =>
            canRemoveEntity
              ? [
                  {
                    label: LangEnum.GLOBAL_REMOVE,
                    testId: CatalogTestIds.CATALOG_CREATION_MODAL_ACCESS_REMOVE,
                    icon: <Icon icon={Trash2} size="sm" />,
                    onClick: () => onRemoveRow(item.id),
                  },
                ]
              : []
          }
        />
      )}
    </Box>
  );
};

export default CatalogCreationAccessTable;
